
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Wand, ChevronDown } from "lucide-react";

interface SampleMessage {
  title: string;
  merchant: string;
  product: string;
  amount: number;
  issue: string;
  detailedIssue: string;
  orderNumber?: string;
}

const sampleMessages: SampleMessage[] = [
  {
    title: "Defective Product",
    merchant: "Amazon",
    product: "Wireless Headphones",
    amount: 89.99,
    issue: "Product arrived damaged",
    detailedIssue: "I received the wireless headphones I ordered, but they arrived with significant damage. The left ear piece has completely lost sound functionality, and the charging case won't hold a charge despite following all setup instructions carefully. I've tried resetting the device multiple times as suggested in the manual, but the issues persist. This appears to be a manufacturing defect as the product failed immediately upon arrival, well within the expected lifespan for this type of electronic device.",
    orderNumber: "112-1234567-1234567"
  },
  {
    title: "Wrong Item",
    merchant: "eBay",
    product: "iPhone Case",
    amount: 24.99,
    issue: "Received wrong item",
    detailedIssue: "I specifically ordered a blue iPhone 14 case as shown in the product listing and photos, but instead received a red iPhone 13 case. The item doesn't fit my phone at all due to the different model size and camera layout. I've double-checked my order confirmation which clearly shows I ordered the correct blue iPhone 14 case. This appears to be a fulfillment error on the seller's end, and I need the correct item or a full refund.",
    orderNumber: "1234567890"
  },
  {
    title: "Not as Described",
    merchant: "Etsy",
    product: "Handmade Jewelry",
    amount: 45.00,
    issue: "Item not as described",
    detailedIssue: "The necklace was advertised as genuine sterling silver in both the title and detailed product description, but the item I received appears to be silver-plated instead. After wearing it just once for a few hours, it has already turned my skin green, which is a clear indication of poor quality plating over base metal. The listing photos and description were misleading about the actual material quality and craftsmanship.",
  }
];

interface QuickStartSectionProps {
  onSampleSelect: (sample: SampleMessage) => void;
}

const QuickStartSection = ({ onSampleSelect }: QuickStartSectionProps) => {
  const [showQuickStart, setShowQuickStart] = useState(false);

  const handleSampleSelect = (sample: SampleMessage) => {
    console.log('QuickStartSection: Sample selected:', sample);
    console.log('QuickStartSection: Detailed issue:', sample.detailedIssue);
    
    // Use the detailed issue description for the form
    const sampleWithDetailedIssue = {
      ...sample,
      issue: sample.detailedIssue
    };
    
    console.log('QuickStartSection: Calling onSampleSelect with:', sampleWithDetailedIssue);
    onSampleSelect(sampleWithDetailedIssue);
    setShowQuickStart(false);
  };

  return (
    <Collapsible open={showQuickStart} onOpenChange={setShowQuickStart}>
      <CollapsibleTrigger asChild>
        <Button 
          variant="outline" 
          className="w-full justify-between h-auto p-4 mb-4"
        >
          <div className="flex items-center gap-2">
            <Wand className="h-4 w-4" />
            <span className="font-medium">Try a Sample</span>
            <span className="text-sm text-muted-foreground ml-2">
              Start with a pre-filled example
            </span>
          </div>
          <ChevronDown className={`h-4 w-4 transition-transform ${showQuickStart ? 'rotate-180' : ''}`} />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-3 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {sampleMessages.map((sample, index) => (
            <Button
              key={index}
              variant="outline"
              className="h-auto p-3 text-left hover:bg-accent/50 transition-colors"
              onClick={() => handleSampleSelect(sample)}
            >
              <span className="font-medium text-sm">{sample.title}</span>
            </Button>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default QuickStartSection;
export type { SampleMessage };
